import { memo } from 'react';
import type {
  Dentition,
  OdontogramState,
  OdontogramTool,
  ToothSurface,
} from '../odontogram.types';
import { Tooth } from './Tooth';

const PERMANENT_ROWS = {
  upperLeft: [18, 17, 16, 15, 14, 13, 12, 11],
  upperRight: [21, 22, 23, 24, 25, 26, 27, 28],
  lowerLeft: [48, 47, 46, 45, 44, 43, 42, 41],
  lowerRight: [31, 32, 33, 34, 35, 36, 37, 38],
};

const TEMPORARY_ROWS = {
  upperLeft: [55, 54, 53, 52, 51],
  upperRight: [61, 62, 63, 64, 65],
  lowerLeft: [85, 84, 83, 82, 81],
  lowerRight: [71, 72, 73, 74, 75],
};

export type ArchFilter = 'all' | 'upper' | 'lower';

interface ArchRowProps {
  title: string;
  orientation: string;
  leftQuadrant: number;
  rightQuadrant: number;
  leftIds: number[];
  rightIds: number[];
  chart: OdontogramState;
  selectedTool: OdontogramTool;
  labelBelow: boolean;
  onApply: (toothId: number, surface: ToothSurface) => void;
  onSelect: (toothId: number) => void;
}

function ArchRow({
  title,
  orientation,
  leftQuadrant,
  rightQuadrant,
  leftIds,
  rightIds,
  chart,
  selectedTool,
  labelBelow,
  onApply,
  onSelect,
}: ArchRowProps) {
  return (
    <section aria-label={title} className="min-w-max">
      <div className="mb-2 flex items-center justify-between gap-5">
        <div>
          <h3 className="text-xs font-bold uppercase tracking-[0.08em] text-slate-700">{title}</h3>
          <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wide text-slate-400">
            {orientation}
          </p>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-semibold text-slate-500">
          <span className="rounded-full bg-cyan-50 px-2 py-1 text-cyan-800">
            Q{leftQuadrant} · derecha
          </span>
          <span className="rounded-full bg-cyan-50 px-2 py-1 text-cyan-800">
            Q{rightQuadrant} · izquierda
          </span>
        </div>
      </div>

      <div className="relative flex items-start gap-1 rounded-lg bg-slate-50/70 px-2 py-2">
        <div className="flex gap-0.5">
          {leftIds.map((toothId) => (
            <Tooth
              key={toothId}
              tooth={chart[toothId]}
              selectedTool={selectedTool}
              labelBelow={labelBelow}
              onApply={onApply}
              onSelect={onSelect}
            />
          ))}
        </div>

        <div className="relative mx-1 w-3 self-stretch" aria-label="Línea media">
          <span className="absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-cyan-500" />
          <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rounded-full border border-cyan-300 bg-white px-1 py-2 text-[8px] font-bold uppercase tracking-wide text-cyan-700 [writing-mode:vertical-rl]">
            Media
          </span>
        </div>

        <div className="flex gap-0.5">
          {rightIds.map((toothId) => (
            <Tooth
              key={toothId}
              tooth={chart[toothId]}
              selectedTool={selectedTool}
              labelBelow={labelBelow}
              onApply={onApply}
              onSelect={onSelect}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

interface OdontogramGridProps {
  chart: OdontogramState;
  dentition: Dentition;
  archFilter: ArchFilter;
  selectedTool: OdontogramTool;
  onApply: (toothId: number, surface: ToothSurface) => void;
  onSelect: (toothId: number) => void;
}

function OdontogramGridComponent({
  chart,
  dentition,
  archFilter,
  selectedTool,
  onApply,
  onSelect,
}: OdontogramGridProps) {
  const rows = dentition === 'permanent' ? PERMANENT_ROWS : TEMPORARY_ROWS;
  const isPermanent = dentition === 'permanent';
  const showUpper = archFilter === 'all' || archFilter === 'upper';
  const showLower = archFilter === 'all' || archFilter === 'lower';

  return (
    <section
      className="overflow-x-auto rounded-xl border border-slate-200 bg-white p-4 shadow-sm"
      aria-label={`Odontograma de dentición ${dentition === 'permanent' ? 'permanente' : 'temporal'}`}
    >
      <div className="mx-auto w-max min-w-full">
        <div className="mb-4 grid min-w-[760px] grid-cols-2 divide-x divide-slate-300 rounded-lg border border-slate-300 bg-white py-2 text-center">
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-600">
            Derecha del paciente
          </span>
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-slate-600">
            Izquierda del paciente
          </span>
        </div>

        <div className="flex flex-col items-center">
          {showUpper ? (
            <ArchRow
              title="Arcada superior"
              orientation="Vestibular arriba"
              leftQuadrant={isPermanent ? 1 : 5}
              rightQuadrant={isPermanent ? 2 : 6}
              leftIds={rows.upperLeft}
              rightIds={rows.upperRight}
              chart={chart}
              selectedTool={selectedTool}
              labelBelow={false}
              onApply={onApply}
              onSelect={onSelect}
            />
          ) : null}

          {showUpper && showLower ? (
            <div className="my-4 h-px w-full min-w-[760px] bg-slate-200" aria-hidden="true" />
          ) : null}

          {showLower ? (
            <ArchRow
              title="Arcada inferior"
              orientation="Vestibular abajo"
              leftQuadrant={isPermanent ? 4 : 8}
              rightQuadrant={isPermanent ? 3 : 7}
              leftIds={rows.lowerLeft}
              rightIds={rows.lowerRight}
              chart={chart}
              selectedTool={selectedTool}
              labelBelow
              onApply={onApply}
              onSelect={onSelect}
            />
          ) : null}
        </div>
      </div>
    </section>
  );
}

export const OdontogramGrid = memo(OdontogramGridComponent);
